import { NextPage } from "next";
import { useRouter } from "next/router";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  Badge,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import { FaPlay, FaCode, FaBalanceScale, FaRocket } from "react-icons/fa";
import VersionInfo from "../components/VersionInfo";

const Home: NextPage = () => {
  const router = useRouter();

  const demos = [
    {
      title: "Canvas Timeline 演示",
      description: "体验新的 Canvas 时间线功能，支持高性能渲染和流畅交互",
      path: "/canvas-timeline",
      icon: FaRocket,
      color: "green",
      features: [
        "🎨 Canvas 2D 渲染",
        "🔍 流畅缩放",
        "👆 拖拽平移",
        "⚡ 高性能",
      ],
    },
    {
      title: "传统 DOM Timeline",
      description: "使用原有的 DOM 时间线实现，兼容性好",
      path: "/dom-timeline",
      icon: FaCode,
      color: "blue",
      features: ["🏗️ DOM 元素", "📱 兼容性好", "🎯 稳定可靠", "🔧 易于调试"],
    },
    {
      title: "性能对比",
      description: "同时显示两种时间线实现，对比性能和体验差异",
      path: "/comparison",
      icon: FaBalanceScale,
      color: "purple",
      features: ["📊 性能对比", "🔄 实时切换", "📈 指标分析", "🎯 体验差异"],
    },
  ];

  return (
    <Box bg="gray.900" minHeight="100vh" color="white" p={{ base: 4, md: 8 }}>
      <VStack spacing={{ base: 6, md: 8 }} maxWidth="1200px" mx="auto">
        {/* 头部 */}
        <VStack spacing={{ base: 3, md: 4 }} textAlign="center" px={{ base: 2, md: 0 }}>
          <Heading
            size={{ base: "xl", md: "2xl" }}
            color="blue.300"
            lineHeight="shorter"
          >
            🎬 TCEngine Motion Timeline
          </Heading>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="gray.400"
            maxWidth={{ base: "100%", md: "600px" }}
            px={{ base: 2, md: 0 }}
          >
            体验新的 Canvas 时间线功能，对比传统 DOM 实现的性能和交互体验差异
          </Text>
          <HStack
            spacing={{ base: 2, md: 4 }}
            wrap="wrap"
            justify="center"
            px={{ base: 2, md: 0 }}
          >
            <Badge
              colorScheme="green"
              fontSize={{ base: "xs", md: "sm" }}
              px={{ base: 2, md: 3 }}
              py={1}
            >
              Canvas 2D
            </Badge>
            <Badge
              colorScheme="blue"
              fontSize={{ base: "xs", md: "sm" }}
              px={{ base: 2, md: 3 }}
              py={1}
            >
              React Hooks
            </Badge>
            <Badge
              colorScheme="purple"
              fontSize={{ base: "xs", md: "sm" }}
              px={{ base: 2, md: 3 }}
              py={1}
            >
              TypeScript
            </Badge>
            <Badge
              colorScheme="orange"
              fontSize={{ base: "xs", md: "sm" }}
              px={{ base: 2, md: 3 }}
              py={1}
            >
              Recoil
            </Badge>
          </HStack>
        </VStack>

        {/* 演示选项 */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={{ base: 4, md: 6 }}
          width="100%"
          px={{ base: 2, md: 0 }}
        >
          {demos.map((demo, index) => (
            <Card
              key={index}
              bg="gray.800"
              borderColor="gray.700"
              borderWidth="1px"
              borderRadius={{ base: "lg", md: "md" }}
              _hover={{
                borderColor: `${demo.color}.500`,
                transform: { base: "none", md: "translateY(-2px)" },
                boxShadow: { base: "none", md: "lg" },
              }}
              transition="all 0.2s"
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                  <HStack spacing={{ base: 2, md: 3 }} align="start">
                    <Icon
                      as={demo.icon}
                      boxSize={{ base: 5, md: 6 }}
                      color={`${demo.color}.400`}
                      mt={1}
                    />
                    <VStack align="start" spacing={{ base: 1, md: 1 }} flex={1}>
                      <Heading
                        size={{ base: "sm", md: "md" }}
                        color={`${demo.color}.300`}
                        lineHeight="shorter"
                      >
                        {demo.title}
                      </Heading>
                      <Text
                        fontSize={{ base: "xs", md: "sm" }}
                        color="gray.400"
                        lineHeight="base"
                      >
                        {demo.description}
                      </Text>
                    </VStack>
                  </HStack>

                  <VStack spacing={{ base: 1, md: 2 }} align="stretch">
                    {demo.features.map((feature, i) => (
                      <Text
                        key={i}
                        fontSize={{ base: "xs", md: "sm" }}
                        color="gray.300"
                        lineHeight="base"
                      >
                        {feature}
                      </Text>
                    ))}
                  </VStack>

                  <Button
                    colorScheme={demo.color}
                    size={{ base: "md", md: "md" }}
                    leftIcon={<FaPlay />}
                    onClick={() => router.push(demo.path)}
                    _hover={{
                      transform: { base: "scale(0.98)", md: "translateY(-2px)" },
                      boxShadow: { base: "md", md: "lg" },
                    }}
                    _active={{
                      transform: "scale(0.96)",
                    }}
                    transition="all 0.2s"
                    w="100%"
                    mt={{ base: 2, md: 0 }}
                  >
                    开始体验
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>

        {/* 快速开始 */}
        <Box
          bg="gray.800"
          borderRadius={{ base: "lg", md: "md" }}
          p={{ base: 4, md: 6 }}
          borderColor="gray.700"
          borderWidth="1px"
          width="100%"
          mx={{ base: 2, md: 0 }}
        >
          <VStack spacing={{ base: 3, md: 4 }}>
            <Heading
              size={{ base: "md", md: "lg" }}
              color="yellow.300"
              textAlign="center"
            >
              🚀 快速开始
            </Heading>
            <Text
              color="gray.400"
              textAlign="center"
              fontSize={{ base: "sm", md: "md" }}
              px={{ base: 2, md: 0 }}
            >
              推荐先体验 Canvas Timeline 演示，感受高性能时间线的流畅交互
            </Text>
            <VStack
              spacing={{ base: 3, md: 0 }}
              direction={{ base: "column", md: "row" }}
              w="100%"
            >
              <Button
                colorScheme="green"
                size={{ base: "md", md: "lg" }}
                leftIcon={<FaRocket />}
                onClick={() => router.push("/canvas-timeline")}
                w={{ base: "100%", md: "auto" }}
                _hover={{
                  transform: { base: "scale(0.98)", md: "translateY(-2px)" },
                  boxShadow: "lg",
                }}
                _active={{
                  transform: "scale(0.96)",
                }}
                transition="all 0.2s"
              >
                体验 Canvas Timeline
              </Button>
              <Button
                variant="outline"
                colorScheme="purple"
                size={{ base: "md", md: "lg" }}
                leftIcon={<FaBalanceScale />}
                onClick={() => router.push("/comparison")}
                w={{ base: "100%", md: "auto" }}
                _hover={{
                  transform: { base: "scale(0.98)", md: "translateY(-2px)" },
                  boxShadow: "lg",
                }}
                _active={{
                  transform: "scale(0.96)",
                }}
                transition="all 0.2s"
              >
                性能对比
              </Button>
            </VStack>
          </VStack>
        </Box>

        {/* 技术说明 */}
        <Box
          bg="gray.800"
          borderRadius={{ base: "lg", md: "md" }}
          p={{ base: 4, md: 6 }}
          borderColor="gray.700"
          borderWidth="1px"
          width="100%"
          mx={{ base: 2, md: 0 }}
        >
          <VStack spacing={{ base: 3, md: 4 }}>
            <Heading
              size={{ base: "sm", md: "md" }}
              color="blue.300"
              textAlign="center"
            >
              💡 技术亮点
            </Heading>
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={{ base: 4, md: 4 }}
              width="100%"
            >
              <Box>
                <Text
                  fontWeight="bold"
                  color="green.400"
                  mb={{ base: 2, md: 2 }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  Canvas Timeline 优势
                </Text>
                <VStack align="start" spacing={{ base: 1, md: 1 }}>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • 高性能 Canvas 2D 渲染
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • 流畅的缩放和平移
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • 实时同步播放器状态
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • 支持大量片段渲染
                  </Text>
                </VStack>
              </Box>
              <Box>
                <Text
                  fontWeight="bold"
                  color="blue.400"
                  mb={{ base: 2, md: 2 }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  实现特点
                </Text>
                <VStack align="start" spacing={{ base: 1, md: 1 }}>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • TypeScript 严格类型检查
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • React Hooks 状态管理
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • Recoil 全局状态同步
                  </Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                    • 支持 EditDraft 数据格式
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Box>

        {/* 版本信息 */}
        <VersionInfo variant="full" />
      </VStack>
    </Box>
  );
};

export default Home;
